/**
 * A JSON value, highlighted with the design system's syntax colours.
 *
 * Built out of React elements rather than an HTML string. The content is a
 * provider payload — data we did not author and cannot vet — so it is never
 * handed to a parser that could execute part of it.
 */

import { Fragment, type ReactNode } from "react";

/** Matches one token: a string (key or value), a number, a literal, or punctuation. */
const TOKEN = /("(?:\\.|[^"\\])*"\s*:?)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b|([{}[\],:])/g;

export function JsonBlock({ value }: { value: unknown }) {
  const source = stringify(value);
  return <pre className="oc-code">{highlight(source)}</pre>;
}

/**
 * Providers sometimes hand back a JSON document as a string. Rendering the
 * escaped form is unreadable, so parse one level in when it is clearly JSON.
 */
function stringify(value: unknown): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function highlight(source: string): ReactNode[] {
  const out: ReactNode[] = [];
  let index = 0;
  let key = 0;

  for (const match of source.matchAll(TOKEN)) {
    const start = match.index;
    if (start > index) out.push(source.slice(index, start));

    const [token, str, num, literal, punct] = match;

    if (str !== undefined) {
      // A string followed by a colon is a key; the colon is punctuation.
      const isKey = str.trimEnd().endsWith(":");
      if (isKey) {
        const colonAt = str.lastIndexOf(":");
        out.push(
          <Fragment key={key++}>
            <span className="k">{str.slice(0, colonAt)}</span>
            <span className="p">{str.slice(colonAt)}</span>
          </Fragment>,
        );
      } else {
        out.push(
          <span className="s" key={key++}>
            {str}
          </span>,
        );
      }
    } else if (num !== undefined || literal !== undefined) {
      out.push(
        <span className="n" key={key++}>
          {num ?? literal}
        </span>,
      );
    } else if (punct !== undefined) {
      out.push(
        <span className="p" key={key++}>
          {punct}
        </span>,
      );
    }

    index = start + token.length;
  }

  if (index < source.length) out.push(source.slice(index));
  return out;
}
